/** @format */

import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  StarIcon,
  ShareIcon,
  CurrencyDollarIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarIconSolid } from "@heroicons/react/24/solid";
import { Course } from "../types";
import { generateCourseAffiliateLink, generateAffiliateText } from "../utils/formatUtils";


import { CourseBook, CourseSection } from "../types";
import { useShare } from "../hooks/useShare";
import { useAuth } from "../contexts/AuthContext";
import { useCourseProgress } from "../hooks/useCourseProgress";
import { useEnrolledCourses } from "../hooks/useEnrolledCourses";
import { useUsers } from "../hooks/useUsers";
import UserProfileLink from "./UserProfileLink";


interface CourseDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  course: Course | null;
  onAddToCart: (course: Course) => void;
  onToggleFavorite: (course: Course) => void;
  isFavorite: boolean;
  isInCart: boolean;
}

const CourseDetailsModal: React.FC<CourseDetailsModalProps> = ({
  isOpen,
  onClose,
  course,
  onAddToCart,
  onToggleFavorite,
  isFavorite = false,
  isInCart = false,
}) => {
  const { currentUser } = useAuth();
  const { handleShare } = useShare();
  const { fetchUsers } = useUsers();

  // Progress and enrollment state
  const { getOverallProgress } = useCourseProgress(course?.id || "");
  const progress = course ? getOverallProgress() : 0;
  const { enrolledCourses } = useEnrolledCourses(currentUser?.uid);
  const isEnrolled = !!(course && enrolledCourses.some(e => e.course_id === course.id));
  const isFreeCourse = !!(course && (course.pricee === "0" || course.pricee === "free" || Number(course.pricee || '0') === 0));

  const [courseCategory, setCourseCategory] = useState<{ id: string; name: string; image_url: string; created_at: any } | null>(null);
  const [expandedSections, setExpandedSections] = useState<{
    requirements: boolean;
    metaLearnings: boolean;
    metaRequirements: boolean;
  }>({
    requirements: false,
    metaLearnings: false,
    metaRequirements: false,
  });

  // Set category when course changes
  useEffect(() => {
    if (!course) return;

    // cat_id now contains the category name directly, no need to fetch from categories collection
    if (course.cat_id) {
      setCourseCategory({ id: course.cat_id, name: course.cat_id, image_url: "", created_at: null });
    } else {
      setCourseCategory(null);
    }
  }, [course?.id, course?.cat_id]);

  // Fetch course author data when course changes
  useEffect(() => {
    if (course?.author?.id) {
      fetchUsers([course.author.id]);
    }
  }, [course?.author?.id, fetchUsers]);



  // Helper function to safely render HTML content
  const renderHTMLContent = (content: string) => {
    if (!content) return null;

    // Check if content contains HTML tags
    if (content.includes("<") && content.includes(">")) {
      return (
        <div
          className="prose prose-sm max-w-none break-words overflow-hidden"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }

    // For plain text, wrap in paragraph with proper text handling
    return (
      <p className="text-gray-700 leading-relaxed text-sm sm:text-base break-words overflow-hidden">
        {content}
      </p>
    );
  };

  // Helper function to get clean text from HTML content
  const getCleanText = (htmlContent: string) => {
    if (!htmlContent) return "";
    return htmlContent.replace(/<[^>]*>/g, "");
  };

  // Helper function to get section display text
  const getSectionDisplayText = (section: string | CourseSection) => {
    if (typeof section === "string") return section;
    return section.name || `Section ${section.order}`;
  };

  // Helper function to get book display text
  const getBookDisplayText = (book: string | CourseBook) => {
    if (typeof book === "string") return book;
    return book.title || "Untitled Book";
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <span key={star}>
            {rating >= star ? (
              <StarIconSolid className="h-5 w-5 text-yellow-400" />
            ) : (
              <StarIcon className="h-5 w-5 text-gray-300" />
            )}
          </span>
        ))}
        <span className="text-sm text-gray-600 ml-2">({rating})</span>
      </div>
    );
  };

  // Don't render if course is null or modal is not open
  if (!isOpen || !course) {
    return null;
  }

  // Share functionality with affiliate code
  const handleShareClick = async () => {
    const courseUrl = generateCourseAffiliateLink(course.id, currentUser?.referralCode);
    const shareText = generateAffiliateText(course.name, currentUser?.referralCode);

    await handleShare({
      title: course.name,
      text: shareText,
      url: courseUrl,
    });
  };

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        ></div>

        <div className="relative w-full max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden transform transition-all">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 sm:px-8 sm:py-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3 mb-2">
                  {course.program && (
                    <div className="flex items-center space-x-2">
                      {Array.isArray(course.program) ? (
                        course.program.map((programType, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white bg-opacity-20 text-white border border-white border-opacity-30"
                          >
                            {programType}
                          </span>
                        ))
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white bg-opacity-20 text-white border border-white border-opacity-30">
                          {course.program}
                        </span>
                      )}
                    </div>
                  )}
                  {course.status && (
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        course.status === "live"
                          ? "bg-green-500 bg-opacity-20 text-green-100 border border-green-300"
                          : course.status === "draft"
                          ? "bg-green-500 bg-opacity-20 text-green-100 border border-green-300"
                          : "bg-yellow-500 bg-opacity-20 text-yellow-100 border border-yellow-300"
                      }`}
                    >
                      {course.status}
                    </span>
                  )}
                </div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white truncate">
                  {getCleanText(course.summary) || course.name}
                </h2>
                <p className="text-blue-100 text-sm sm:text-base mt-1 truncate">
                  by {course.author?.name || "Unknown Author"}
                </p>
              </div>

              <div className="flex items-center space-x-2 ml-4">
                {/* Share Button */}
                <button
                  onClick={handleShareClick}
                  className="p-2 bg-white bg-opacity-20 rounded-lg transition-all duration-200 group"
                  title="Share course"
                >
                  <ShareIcon className="h-5 w-5 text-white" />
                </button>

                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="p-2 bg-white bg-opacity-20 rounded-lg transition-all duration-200"
                  title="Close modal"
                >
                  <XMarkIcon className="h-5 w-5 text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="max-h-[70vh] overflow-y-auto">
            <div className="p-4 sm:p-6 lg:p-8 space-y-6">
              {/* Course Image and Basic Info */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Image */}
                <div className="lg:col-span-1">
                  <div className="relative group">
                    <img
                      src={course.image_url || "/placeholder-course.jpg"}
                      alt={course.name}
                      className="w-full h-48 sm:h-56 lg:h-64 object-cover rounded-xl shadow-lg transition-all duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-xl"></div>
                  </div>

                  {/* Video Preview */}
                  {course.video_url && (
                    <div className="mt-4">
                      <h4 className="text-sm sm:text-base font-semibold text-gray-900 mb-2 flex items-center">
                        <svg
                          className="w-4 h-4 sm:w-5 sm:h-5 text-red-500 mr-2"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        Course Preview
                      </h4>
                      <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                        <video
                          src={course.video_url}
                          controls
                          className="w-full h-32 sm:h-40 object-cover"
                          poster={course.image_url || "/placeholder-course.jpg"}
                        >
                          <source src={course.video_url} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    </div>
                  )}
                </div>

                {/* Basic Info */}
                <div className="lg:col-span-2 space-y-4">
                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-3 sm:p-4 text-center border border-blue-200">
                      <div className="text-lg sm:text-xl font-bold text-blue-600">
                        {course.lessons_count || 0}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        Lessons
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-3 sm:p-4 text-center border border-green-200">
                      <div className="text-lg sm:text-xl font-bold text-green-600">
                        {course.books?.length || 0}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        Books
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-3 sm:p-4 text-center border border-purple-200">
                      <div className="text-lg sm:text-xl font-bold text-purple-600">
                        {course.sections?.length || 0}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        Sections
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg p-3 sm:p-4 text-center border border-orange-200">
                      <div className="text-lg sm:text-xl font-bold text-orange-600">
                        {course.students || 0}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        Students
                      </div>
                    </div>
                  </div>

                  {/* Additional Course Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                    <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-3 sm:p-4 text-center border border-cyan-200">
                      <div className="text-sm sm:text-base font-semibold text-cyan-700">
                        {course.language || "Not specified"}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        Language
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg p-3 sm:p-4 text-center border border-emerald-200">
                      <div className="text-sm sm:text-base font-semibold text-emerald-700">
                        {course.duration || "Not specified"}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        Duration
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-lg p-3 sm:p-4 text-center border border-violet-200">
                      <div className="text-sm sm:text-base font-semibold text-violet-700">
                        {course.created_at
                          ? new Date(
                              course.created_at._seconds * 1000
                            ).toLocaleDateString()
                          : "Not specified"}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-600">
                        Created
                      </div>
                    </div>
                  </div>

                  {/* Program and Category Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    {course.cat_id && (
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-3 sm:p-4 border border-amber-200">
                        {courseCategory ? (
                          <div className="text-center">
                            <div className="text-sm sm:text-base font-semibold text-amber-700">
                              {courseCategory.name}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600">
                              Category
                            </div>
                          </div>
                        ) : (
                          <div className="text-center">
                            <div className="text-sm sm:text-base font-semibold text-amber-700">
                              {course.cat_id}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-600">
                              Category ID
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {course.program && (
                      <div className="bg-gradient-to-br from-rose-50 to-pink-50 rounded-lg p-3 sm:p-4 text-center border border-rose-200">
                        <div className="text-sm sm:text-base font-semibold text-rose-700">
                          {Array.isArray(course.program) ? course.program.join(", ") : course.program}
                        </div>
                        <div className="text-xs sm:text-sm text-gray-600">
                          Program{Array.isArray(course.program) && course.program.length > 1 ? "s" : ""}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Price and Status */}
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-lg font-bold text-lg">
                      {course.pricee === "0" || course.pricee === "free"
                        ? "Free"
                        : `$${course.pricee}`}
                    </div>
                    {course.status && (
                      <div
                        className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                          course.status === "live"
                            ? "bg-green-100 text-green-800"
                            : course.status === "draft"
                            ? "bg-gray-100 text-gray-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {course.status}
                      </div>
                    )}
                  </div>

                  {/* Author Info */}
                  {course.author && (
                    <div className="flex items-center space-x-3 bg-gray-50 rounded-lg p-3 sm:p-4">
                      <img
                        src={
                          course.author.image_url || "/placeholder-avatar.jpg"
                        }
                        alt={course.author.name}
                        className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-semibold text-gray-900 text-sm sm:text-base">
                          <UserProfileLink 
                            userId={course.author.id}
                            fallbackName={course.author.name}
                            fallbackAvatarUrl={course.author.image_url}
                            className="hover:text-primary-600 transition-colors"
                          />
                        </div>
                        <div className="text-gray-600 text-xs sm:text-sm">
                          Course Instructor
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Course Details Sections */}
              <div className="space-y-6">
                {/* Requirements */}
                {course.requirements && (
                  <div>
                    <button
                      onClick={() =>
                        setExpandedSections((prev) => ({
                          ...prev,
                          requirements: !prev.requirements,
                        }))
                      }
                      className="w-full text-left"
                    >
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center justify-between group">
                        <div className="flex items-center">
                          <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-lg blur-sm opacity-75"></div>
                            <div className="relative bg-gradient-to-r from-blue-500 to-indigo-500 p-2 rounded-lg">
                              <svg
                                className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </div>
                          </div>
                          <span className="ml-2 sm:ml-3 text-base sm:text-lg">
                            Requirements
                          </span>
                        </div>
                        <div
                          className={`transform transition-transform duration-300 ${
                            expandedSections.requirements ? "rotate-180" : ""
                          }`}
                        >
                          <svg
                            className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </h3>
                    </button>
                    <div
                      className={`bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl overflow-hidden transition-all duration-300 border border-blue-100 ${
                        expandedSections.requirements
                          ? "max-h-96 opacity-100"
                          : "max-h-0 opacity-0"
                      }`}
                    >
                      <div className="p-4 sm:p-6">
                        <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                          {course.requirements}
                        </p>
                      </div>
                    </div>
                  </div>
                )}



                {/* Course Content */}
                {course.sections && course.sections.length > 0 && (
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 to-purple-400 rounded-lg blur-sm opacity-75"></div>
                        <div className="relative bg-gradient-to-r from-indigo-500 to-purple-500 p-2 rounded-lg">
                          <svg
                            className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                            />
                          </svg>
                        </div>
                      </div>
                      <span className="ml-2 sm:ml-3 text-base sm:text-lg">
                        Course Content
                      </span>
                    </h3>
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 sm:p-6 border border-indigo-100">
                      <div className="space-y-2">
                        {course.sections.map((section, index) => (
                          <div
                            key={index}
                            className="flex items-center space-x-3 bg-white rounded-lg p-3 shadow-sm border border-indigo-200"
                          >
                            <div className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0" />
                            <span className="text-gray-700 text-sm sm:text-base">
                              {getSectionDisplayText(section)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Course Program */}
                {course.sections && course.sections.length > 0 && (
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-teal-400 to-blue-400 rounded-lg blur-sm opacity-75"></div>
                        <div className="relative bg-gradient-to-r from-teal-500 to-blue-500 p-2 rounded-lg">
                          <svg
                            className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                            />
                          </svg>
                        </div>
                      </div>
                      <span className="ml-2 sm:ml-3 text-base sm:text-lg">
                        Course Program
                      </span>
                    </h3>
                    <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-4 sm:p-6 border border-teal-100">
                      <div className="space-y-4">
                        {course.sections.map((section, sectionIndex) => (
                          <div
                            key={sectionIndex}
                            className="bg-white rounded-lg p-4 shadow-sm border border-teal-200"
                          >
                            {/* Section Header */}
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="font-semibold text-gray-900 text-sm sm:text-base flex items-center">
                                <div className="w-6 h-6 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full flex items-center justify-center mr-3 text-white text-xs font-bold">
                                  {sectionIndex + 1}
                                </div>
                                {getSectionDisplayText(section)}
                              </h4>
                              {typeof section === "object" &&
                                section.lessons && (
                                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                                    {section.lessons.length}{" "}
                                    {section.lessons.length === 1
                                      ? "lesson"
                                      : "lessons"}
                                  </span>
                                )}
                            </div>

                            {/* Lessons */}
                            {typeof section === "object" &&
                              section.lessons &&
                              section.lessons.length > 0 && (
                                <div className="space-y-2 ml-9">
                                  {section.lessons.map(
                                    (lesson, lessonIndex) => (
                                      <div
                                        key={lessonIndex}
                                        className="flex items-start space-x-3"
                                      >
                                        <div className="w-1.5 h-1.5 bg-teal-400 rounded-full mt-2 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center justify-between">
                                            <span className="text-gray-700 text-sm font-medium">
                                              {lesson.name ||
                                                `Lesson ${lessonIndex + 1}`}
                                            </span>
                                            {lesson.content_type && (
                                              <span className="text-xs text-gray-500 bg-blue-50 px-2 py-1 rounded-full">
                                                {lesson.content_type}
                                              </span>
                                            )}
                                          </div>
                                          {lesson.description && (
                                            <p className="text-gray-600 text-xs mt-1 line-clamp-2">
                                              {lesson.description}
                                            </p>
                                          )}
                                          {lesson.video_url && (
                                            <div className="flex items-center mt-2">
                                              <svg
                                                className="w-3 h-3 text-red-500 mr-1"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path d="M8 5v14l11-7z" />
                                              </svg>
                                              <span className="text-xs text-gray-500">
                                                Video available
                                              </span>
                                            </div>
                                          )}
                                          {lesson.quiz &&
                                            lesson.quiz.length > 0 && (
                                              <div className="flex items-center mt-2">
                                                <svg
                                                  className="w-3 h-3 text-yellow-500 mr-1"
                                                  fill="currentColor"
                                                  viewBox="0 0 24 24"
                                                >
                                                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <span className="text-xs text-gray-500">
                                                  {lesson.quiz.length} quiz
                                                  questions
                                                </span>
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Included Books */}
                {course.books && course.books.length > 0 && (
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-lg blur-sm opacity-75"></div>
                        <div className="relative bg-gradient-to-r from-blue-500 to-cyan-500 p-2 rounded-lg">
                          <svg
                            className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                            />
                          </svg>
                        </div>
                      </div>
                      <span className="ml-2 sm:ml-3 text-base sm:text-lg">
                        Included Books ({course.books.length})
                      </span>
                    </h3>
                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 sm:p-6 border border-blue-100">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {course.books.map((book, index) => (
                          <div
                            key={index}
                            className="bg-white rounded-lg p-4 shadow-sm border border-blue-200 transition-shadow duration-200"
                          >
                            {/* Book Image */}
                            <div className="mb-3">
                              <img
                                src={
                                  typeof book === "string"
                                    ? "/placeholder-book.jpg"
                                    : book.image_url || "/placeholder-book.jpg"
                                }
                                alt={
                                  typeof book === "string"
                                    ? book
                                    : book.title || "Book"
                                }
                                className="w-full h-32 object-cover rounded-lg"
                              />
                            </div>

                            {/* Book Details */}
                            <div className="space-y-2">
                              <h4 className="font-semibold text-gray-900 text-sm sm:text-base line-clamp-2">
                                {typeof book === "string"
                                  ? book
                                  : book.title || "Untitled Book"}
                              </h4>

                              {typeof book === "object" && book.description && (
                                <p className="text-gray-600 text-xs sm:text-sm line-clamp-3">
                                  {book.description}
                                </p>
                              )}

                              {typeof book === "object" && book.author && (
                                <p className="text-blue-600 text-xs sm:text-sm font-medium">
                                  by {book.author}
                                </p>
                              )}

                              {typeof book === "object" && book.amount && (
                                <div className="flex items-center justify-between pt-2">
                                  <span className="text-green-600 font-semibold text-sm">
                                    ${book.amount}
                                  </span>
                                  {book.pdf_url && (
                                    <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
                                      <svg
                                        className="w-3 h-3 mr-1"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      PDF Available
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Meta Information */}
                {course.meta && (
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 rounded-lg blur-sm opacity-75"></div>
                        <div className="relative bg-gradient-to-r from-purple-500 to-pink-500 p-2 rounded-lg">
                          <svg
                            className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                      </div>
                      <span className="ml-2 sm:ml-3 text-base sm:text-lg">
                        Additional Information
                      </span>
                    </h3>
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 sm:p-6 space-y-4 border border-purple-100">
                      {course.meta.description && (
                        <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border border-purple-200 overflow-hidden">
                          <h4 className="font-semibold text-gray-900 mb-2 flex items-center text-sm sm:text-base">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-blue-400 to-blue-500 rounded-lg flex items-center justify-center mr-2 sm:mr-3">
                              <svg
                                className="w-3 h-3 sm:w-4 sm:h-4 text-white"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            </div>
                            Meta Description
                          </h4>
                          <div className="break-words overflow-hidden text-wrap">
                            {getCleanText(course.meta.description)}
                          </div>
                        </div>
                      )}

                      {course.meta.learnings &&
                        course.meta.learnings.length > 0 && (
                          <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border border-purple-200">
                            <button
                              onClick={() =>
                                setExpandedSections((prev) => ({
                                  ...prev,
                                  metaLearnings: !prev.metaLearnings,
                                }))
                              }
                              className="w-full text-left"
                            >
                              <h4 className="font-semibold text-gray-900 mb-2 flex items-center justify-between group text-sm sm:text-base">
                                <div className="flex items-center">
                                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-green-400 to-green-500 rounded-lg flex items-center justify-center mr-2 sm:mr-3">
                                    <svg
                                      className="w-3 h-3 sm:w-4 sm:h-4 text-white"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                                      />
                                    </svg>
                                  </div>
                                  <span>Meta Learnings</span>
                                </div>
                                <div
                                  className={`transform transition-transform duration-300 ${
                                    expandedSections.metaLearnings
                                      ? "rotate-180"
                                      : ""
                                  }`}
                                >
                                  <svg
                                    className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                </div>
                              </h4>
                            </button>
                            <div
                              className={`overflow-hidden transition-all duration-300 ${
                                expandedSections.metaLearnings
                                  ? "max-h-96 opacity-100"
                                  : "max-h-0 opacity-0"
                              }`}
                            >
                              <div className="space-y-2">
                                {course.meta.learnings.map(
                                  (learning, index) => (
                                    <div
                                      key={index}
                                      className="flex items-start space-x-3 bg-green-50 rounded-lg p-3"
                                    >
                                      <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                                      <span className="text-gray-700 text-sm sm:text-base">
                                        {learning}
                                      </span>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                      {course.meta.requirements &&
                        course.meta.requirements.length > 0 && (
                          <div className="bg-white rounded-lg p-3 sm:p-4 shadow-sm border border-purple-200">
                            <button
                              onClick={() =>
                                setExpandedSections((prev) => ({
                                  ...prev,
                                  metaRequirements: !prev.metaRequirements,
                                }))
                              }
                              className="w-full text-left"
                            >
                              <h4 className="font-semibold text-gray-900 mb-2 flex items-center justify-between group text-sm sm:text-base">
                                <div className="flex items-center">
                                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg flex items-center justify-center mr-2 sm:mr-3">
                                    <svg
                                      className="w-3 h-3 sm:w-4 sm:h-4 text-white"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                                      />
                                    </svg>
                                  </div>
                                  <span>Meta Requirements</span>
                                </div>
                                <div
                                  className={`transform transition-transform duration-300 ${
                                    expandedSections.metaRequirements
                                      ? "rotate-180"
                                      : ""
                                  }`}
                                >
                                  <svg
                                    className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                </div>
                              </h4>
                            </button>
                            <div
                              className={`overflow-hidden transition-all duration-300 ${
                                expandedSections.metaRequirements
                                  ? "max-h-96 opacity-100"
                                  : "max-h-0 opacity-0"
                              }`}
                            >
                              <div className="space-y-2">
                                {course.meta.requirements.map(
                                  (requirement, index) => (
                                    <div
                                      key={index}
                                      className="flex items-start space-x-3 bg-orange-50 rounded-lg p-3"
                                    >
                                      <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0" />
                                      <span className="text-gray-700 text-sm sm:text-base">
                                        {requirement}
                                      </span>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                )}

                {/* Course Summary */}
                {course.summary && (
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-indigo-400 to-blue-400 rounded-lg blur-sm opacity-75"></div>
                        <div className="relative bg-gradient-to-r from-indigo-500 to-blue-500 p-2 rounded-lg">
                          <svg
                            className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                            />
                          </svg>
                        </div>
                      </div>
                      <span className="ml-2 sm:ml-3 text-base sm:text-lg">
                        Course Summary
                      </span>
                    </h3>
                    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-4 sm:p-6 border border-indigo-100">
                      {getCleanText(course.summary)}
                    </div>
                  </div>
                )}

                {/* Course Learnings */}
                {course.learnings && (
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4 flex items-center">
                      <div className="relative">
                        <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-emerald-400 rounded-lg blur-sm opacity-75"></div>
                        <div className="relative bg-gradient-to-r from-green-500 to-emerald-500 p-2 rounded-lg">
                          <svg
                            className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                            />
                          </svg>
                        </div>
                      </div>
                      <span className="ml-2 sm:ml-3 text-base sm:text-lg">
                        What You'll Learn
                      </span>
                    </h3>
                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 sm:p-6 border border-green-100">
                      <div className="space-y-3">
                        {Array.isArray(course.learnings) ? (
                          course.learnings.map((learning, index) => (
                            <div
                              key={index}
                              className="flex items-start space-x-3"
                            >
                              <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0" />
                              <span className="text-gray-700 text-sm sm:text-base">
                                {getCleanText(learning)}
                              </span>
                            </div>
                          ))
                        ) : (
                          <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                            {getCleanText(course.learnings)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="bg-gray-50 px-4 sm:px-6 lg:px-8 py-4 sm:py-6 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              {/* Share Button */}
              <button
                onClick={handleShareClick}
                className="px-4 sm:px-6 py-2 sm:py-3 border-2 border-gray-300 text-gray-700 rounded-xl transition-all duration-300 font-semibold flex items-center justify-center space-x-2"
              >
                <ShareIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="text-sm sm:text-base">Share</span>
              </button>

              <div className="flex-1" />

              {/* Primary Action: Start/Continue Learning or Enroll */}
              {course && (isFreeCourse || isEnrolled) ? (
                <a
                  href={`/app/course-learning/${course.id}`}
                  className="px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl transition-all duration-300 font-semibold shadow-lg flex items-center justify-center space-x-2"
                >
                  <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5v14l11-7z" />
                  </svg>
                  <span className="text-sm sm:text-base">{progress > 0 ? 'Continue Learning' : 'Start Learning'}</span>
                </a>
              ) : (
                <button className="px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl transition-all duration-300 font-semibold shadow-lg flex items-center justify-center space-x-2">
                  <CurrencyDollarIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="text-sm sm:text-base">Enroll Now</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetailsModal;
